/**
 * HTTP Status Code Constants
 *
 * Comprehensive list of HTTP status codes with documentation.
 * Use these constants instead of magic numbers throughout the codebase.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/HTTP/Status
 * @see https://www.rfc-editor.org/rfc/rfc9110.html#name-status-codes
 */

// ============================================================================
// 2xx Success
// ============================================================================

/**
 * 200 OK - Standard successful HTTP request
 * The request succeeded. The result meaning depends on the HTTP method.
 */
export const HTTP_OK = 200;

/**
 * 201 Created - Resource successfully created
 * The request succeeded and a new resource was created as a result.
 * Typically used as response to POST requests.
 */
export const HTTP_CREATED = 201;

/**
 * 202 Accepted - Request accepted for processing
 * The request has been accepted for processing, but processing has not been completed.
 */
export const HTTP_ACCEPTED = 202;

/**
 * 204 No Content - Successful request with no response body
 * The server successfully processed the request, but is not returning any content.
 * Typically used for DELETE requests.
 */
export const HTTP_NO_CONTENT = 204;

// ============================================================================
// 3xx Redirection
// ============================================================================

/**
 * 301 Moved Permanently - Resource permanently moved
 * The URL of the requested resource has been changed permanently.
 */
export const HTTP_MOVED_PERMANENTLY = 301;

/**
 * 302 Found - Resource temporarily moved
 * The URI of the requested resource has been changed temporarily.
 */
export const HTTP_FOUND = 302;

/**
 * 304 Not Modified - Cached resource still valid
 * Response has not been modified, so the client can continue to use cached version.
 */
export const HTTP_NOT_MODIFIED = 304;

// ============================================================================
// 4xx Client Errors
// ============================================================================

/**
 * 400 Bad Request - Server cannot process request due to client error
 * The server cannot or will not process the request due to malformed syntax,
 * invalid request message framing, or deceptive request routing.
 */
export const HTTP_BAD_REQUEST = 400;

/**
 * 401 Unauthorized - Authentication required
 * The client must authenticate itself to get the requested response.
 * Despite the name, this status code means "unauthenticated".
 */
export const HTTP_UNAUTHORIZED = 401;

/**
 * 403 Forbidden - Server refuses to authorize request
 * The client does not have access rights to the content; unlike 401,
 * the client's identity is known to the server.
 */
export const HTTP_FORBIDDEN = 403;

/**
 * 404 Not Found - Resource not found
 * The server cannot find the requested resource.
 */
export const HTTP_NOT_FOUND = 404;

/**
 * 405 Method Not Allowed - HTTP method not supported for this resource
 * The request method is known by the server but is not supported by the target resource.
 */
export const HTTP_METHOD_NOT_ALLOWED = 405;

/**
 * 408 Request Timeout - Server timed out waiting for request
 * The server would like to shut down this unused connection.
 * **RETRYABLE** - Client should retry the request.
 */
export const HTTP_REQUEST_TIMEOUT = 408;

/**
 * 409 Conflict - Request conflicts with current state
 * The request conflicts with the current state of the server.
 * Often used for database deadlocks or concurrent modification conflicts.
 */
export const HTTP_CONFLICT = 409;

/**
 * 422 Unprocessable Entity - Validation error
 * The request was well-formed but contains semantic errors.
 * Used for validation failures.
 */
export const HTTP_UNPROCESSABLE_ENTITY = 422;

/**
 * 429 Too Many Requests - Rate limit exceeded
 * The user has sent too many requests in a given amount of time.
 * **RETRYABLE** - Client should wait and retry after backoff period.
 */
export const HTTP_TOO_MANY_REQUESTS = 429;

// ============================================================================
// 5xx Server Errors
// ============================================================================

/**
 * 500 Internal Server Error - Generic server error
 * The server has encountered a situation it does not know how to handle.
 */
export const HTTP_INTERNAL_SERVER_ERROR = 500;

/**
 * 501 Not Implemented - Server does not support functionality
 * The request method is not supported by the server and cannot be handled.
 */
export const HTTP_NOT_IMPLEMENTED = 501;

/**
 * 502 Bad Gateway - Invalid response from upstream server
 * The server, while working as a gateway, got an invalid response.
 * **RETRYABLE** - Often transient, client should retry.
 */
export const HTTP_BAD_GATEWAY = 502;

/**
 * 503 Service Unavailable - Server temporarily unavailable
 * The server is not ready to handle the request. Common causes include
 * server maintenance, overload, or temporary unavailability.
 * **RETRYABLE** - Server may recover, client should retry with backoff.
 */
export const HTTP_SERVICE_UNAVAILABLE = 503;

/**
 * 504 Gateway Timeout - Gateway did not receive timely response
 * The server is acting as a gateway and did not get a response in time.
 * **RETRYABLE** - Often transient network issue, client should retry.
 */
export const HTTP_GATEWAY_TIMEOUT = 504;

// ============================================================================
// Status Code Collections
// ============================================================================

/**
 * HTTP status codes that indicate a successful response (2xx range)
 */
export const SUCCESS_STATUS_CODES = [
  HTTP_OK,
  HTTP_CREATED,
  HTTP_ACCEPTED,
  HTTP_NO_CONTENT,
] as const;

/**
 * HTTP status codes that indicate a client error (4xx range)
 */
export const CLIENT_ERROR_STATUS_CODES = [
  HTTP_BAD_REQUEST,
  HTTP_UNAUTHORIZED,
  HTTP_FORBIDDEN,
  HTTP_NOT_FOUND,
  HTTP_METHOD_NOT_ALLOWED,
  HTTP_REQUEST_TIMEOUT,
  HTTP_CONFLICT,
  HTTP_UNPROCESSABLE_ENTITY,
  HTTP_TOO_MANY_REQUESTS,
] as const;

/**
 * HTTP status codes that indicate a server error (5xx range)
 */
export const SERVER_ERROR_STATUS_CODES = [
  HTTP_INTERNAL_SERVER_ERROR,
  HTTP_NOT_IMPLEMENTED,
  HTTP_BAD_GATEWAY,
  HTTP_SERVICE_UNAVAILABLE,
  HTTP_GATEWAY_TIMEOUT,
] as const;

/**
 * HTTP status codes that warrant an automatic retry
 * These indicate transient failures that may succeed on retry:
 * - 408: Request Timeout
 * - 429: Too Many Requests (rate limit)
 * - 502: Bad Gateway (upstream server error)
 * - 503: Service Unavailable (temporary)
 * - 504: Gateway Timeout (network issue)
 */
export const RETRYABLE_STATUS_CODES = [
  HTTP_REQUEST_TIMEOUT,
  HTTP_TOO_MANY_REQUESTS,
  HTTP_BAD_GATEWAY,
  HTTP_SERVICE_UNAVAILABLE,
  HTTP_GATEWAY_TIMEOUT,
] as const;

/**
 * HTTP status codes that should never be retried
 * These indicate permanent failures or client errors:
 * - 400: Bad Request (malformed request)
 * - 401: Unauthorized (authentication required)
 * - 403: Forbidden (insufficient permissions)
 * - 404: Not Found (resource doesn't exist)
 * - 405: Method Not Allowed (wrong HTTP method)
 * - 422: Unprocessable Entity (validation error)
 */
export const NON_RETRYABLE_STATUS_CODES = [
  HTTP_BAD_REQUEST,
  HTTP_UNAUTHORIZED,
  HTTP_FORBIDDEN,
  HTTP_NOT_FOUND,
  HTTP_METHOD_NOT_ALLOWED,
  HTTP_UNPROCESSABLE_ENTITY,
] as const;

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Type representing all possible HTTP status codes
 */
export type HttpStatusCode =
  | typeof HTTP_OK
  | typeof HTTP_CREATED
  | typeof HTTP_ACCEPTED
  | typeof HTTP_NO_CONTENT
  | typeof HTTP_MOVED_PERMANENTLY
  | typeof HTTP_FOUND
  | typeof HTTP_NOT_MODIFIED
  | typeof HTTP_BAD_REQUEST
  | typeof HTTP_UNAUTHORIZED
  | typeof HTTP_FORBIDDEN
  | typeof HTTP_NOT_FOUND
  | typeof HTTP_METHOD_NOT_ALLOWED
  | typeof HTTP_REQUEST_TIMEOUT
  | typeof HTTP_CONFLICT
  | typeof HTTP_UNPROCESSABLE_ENTITY
  | typeof HTTP_TOO_MANY_REQUESTS
  | typeof HTTP_INTERNAL_SERVER_ERROR
  | typeof HTTP_NOT_IMPLEMENTED
  | typeof HTTP_BAD_GATEWAY
  | typeof HTTP_SERVICE_UNAVAILABLE
  | typeof HTTP_GATEWAY_TIMEOUT;

/**
 * Type representing only retryable HTTP status codes
 */
export type RetryableStatusCode = (typeof RETRYABLE_STATUS_CODES)[number];

/**
 * Type representing only non-retryable HTTP status codes
 */
export type NonRetryableStatusCode =
  (typeof NON_RETRYABLE_STATUS_CODES)[number];

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if a status code indicates success (2xx range)
 */
export function isSuccessStatus(status: number): boolean {
  return status >= 200 && status < 300;
}

/**
 * Check if a status code indicates a client error (4xx range)
 */
export function isClientError(status: number): boolean {
  return status >= 400 && status < 500;
}

/**
 * Check if a status code indicates a server error (5xx range)
 */
export function isServerError(status: number): boolean {
  return status >= 500 && status < 600;
}

/**
 * Check if a status code warrants a retry attempt
 */
export function isRetryableStatus(status: number): boolean {
  return RETRYABLE_STATUS_CODES.includes(status as RetryableStatusCode);
}

/**
 * Get a human-readable description of an HTTP status code
 */
export function getStatusDescription(status: number): string {
  const descriptions: Record<number, string> = {
    [HTTP_OK]: 'OK',
    [HTTP_CREATED]: 'Created',
    [HTTP_ACCEPTED]: 'Accepted',
    [HTTP_NO_CONTENT]: 'No Content',
    [HTTP_MOVED_PERMANENTLY]: 'Moved Permanently',
    [HTTP_FOUND]: 'Found',
    [HTTP_NOT_MODIFIED]: 'Not Modified',
    [HTTP_BAD_REQUEST]: 'Bad Request',
    [HTTP_UNAUTHORIZED]: 'Unauthorized',
    [HTTP_FORBIDDEN]: 'Forbidden',
    [HTTP_NOT_FOUND]: 'Not Found',
    [HTTP_METHOD_NOT_ALLOWED]: 'Method Not Allowed',
    [HTTP_REQUEST_TIMEOUT]: 'Request Timeout',
    [HTTP_CONFLICT]: 'Conflict',
    [HTTP_UNPROCESSABLE_ENTITY]: 'Unprocessable Entity',
    [HTTP_TOO_MANY_REQUESTS]: 'Too Many Requests',
    [HTTP_INTERNAL_SERVER_ERROR]: 'Internal Server Error',
    [HTTP_NOT_IMPLEMENTED]: 'Not Implemented',
    [HTTP_BAD_GATEWAY]: 'Bad Gateway',
    [HTTP_SERVICE_UNAVAILABLE]: 'Service Unavailable',
    [HTTP_GATEWAY_TIMEOUT]: 'Gateway Timeout',
  };

  return descriptions[status] || `Unknown Status (${status})`;
}
